import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import {
  RICERCA_TRATTATIVE,
  type RigaTrattativa,
  esportaTrattative,
  useConteggiTrattative,
  useDefinizioneFiltriTrattative,
  useTrattative,
} from '../api/trattative.js';
import { AzioniDiMassa } from '../componenti/AzioniDiMassa.js';
import { BarraFiltri } from '../componenti/BarraFiltri.js';
import { PiedeElenco } from '../componenti/PiedeElenco.js';
import { type Colonna, Tabella } from '../componenti/Tabella.js';
import { giorno } from '../formati.js';
import { useRicerca } from '../useRicerca.js';
import { useSelezione } from '../useSelezione.js';
import { ModaleTrattativa } from './ModaleTrattativa.js';
import { locale, t } from '../i18n/lingua.js';

/**
 * L'elenco delle trattative.
 *
 * Cinque righe: il modulo è di febbraio 2026. Con cinque righe il problema
 * non sono le prestazioni, è dire **a che punto sono** e **quanto valgono** —
 * e la seconda domanda ha due risposte, il budget e il budget pesato per la
 * probabilità, che è quello che si guarda quando si sommano.
 */

export const STATI: Record<string, { testo: string; classe: string }> = {
  in_progress: { testo: t('In corso'), classe: 'badge-warn' },
  won: { testo: t('Vinta'), classe: 'badge-ok' },
  lost: { testo: t('Persa'), classe: '' },
};

const euro = (valore: number | null) =>
  valore === null
    ? null
    : valore.toLocaleString('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });

const COLONNE: Colonna<RigaTrattativa>[] = [
  {
    chiave: 'subject',
    titolo: t('Oggetto'),
    ordinabile: true,
    larghezzaMax: 320,
    cella: (r) => (
      <>
        <Link className="cell-link strong" to="/negotiations/$id" params={{ id: String(r.id) }}>
          {r.subject}
        </Link>
        {r.archived && (
          <span className="badge" style={{ marginLeft: 8 }}>
            {t('archiviata')}
          </span>
        )}
      </>
    ),
  },
  {
    chiave: 'customer',
    titolo: t('Cliente'),
    larghezzaMax: 240,
    cella: (r) => (
      <Link className="cell-link" to="/customers/$id" params={{ id: String(r.customerId) }}>
        {r.customer}
      </Link>
    ),
  },
  {
    chiave: 'status',
    titolo: t('Stato'),
    ordinabile: true,
    cella: (r) => {
      const s = STATI[r.status] ?? { testo: r.status, classe: '' };
      return <span className={`badge ${s.classe}`}>{s.testo}</span>;
    },
  },
  {
    chiave: 'expectedClosingDate',
    titolo: t('Chiusura prevista'),
    ordinabile: true,
    monospazio: true,
    cella: (r) => giorno(r.expectedClosingDate),
  },
  {
    chiave: 'budgetAmount',
    titolo: t('Budget'),
    ordinabile: true,
    allineamento: 'destra',
    monospazio: true,
    cella: (r) => euro(r.budgetAmount) ?? <span className="dim">—</span>,
  },
  {
    chiave: 'probabilityClosingDeal',
    titolo: t('Probabilità'),
    ordinabile: true,
    allineamento: 'destra',
    // una barra invece del solo numero: fra il 25% e il 55% la differenza si
    // vede prima di leggerla
    cella: (r) => (
      <span className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
        <span className="barra-probabilita" aria-hidden="true">
          <span style={{ width: `${r.probabilityClosingDeal}%` }} />
        </span>
        <span className="num">{r.probabilityClosingDeal}%</span>
      </span>
    ),
  },
  {
    chiave: 'weightedAmount',
    titolo: t('Valore pesato'),
    allineamento: 'destra',
    monospazio: true,
    cella: (r) =>
      r.weightedAmount === null ? (
        <span className="dim">—</span>
      ) : (
        euro(Number(r.weightedAmount))
      ),
  },
  {
    chiave: 'activities',
    titolo: t('Attività'),
    allineamento: 'destra',
    monospazio: true,
    cella: (r) => (r.activities > 0 ? r.activities : <span className="dim">—</span>),
  },
  {
    chiave: 'pricelists',
    titolo: t('Proposte'),
    allineamento: 'destra',
    monospazio: true,
    cella: (r) => (r.pricelists > 0 ? r.pricelists : <span className="dim">—</span>),
  },
];

export function Trattative() {
  const { ricerca, testo, setTesto, cambia, vaiA, ordinaPer, filtra } =
    useRicerca(RICERCA_TRATTATIVE);
  const elenco = useTrattative(ricerca);
  const [selezione, setSelezione] = useSelezione(elenco.data?.rows);
  const conteggi = useConteggiTrattative(ricerca);
  const definizioni = useDefinizioneFiltriTrattative();
  const navigate = useNavigate();
  const [nuovaAperta, setNuovaAperta] = useState(false);

  const dati = elenco.data;
  const scelta = ricerca.filters?.status;

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('Trattative')}</h1>
          <div className="page-sub">
            {dati
              ? ricerca.archived
                ? t('{n} trattative, archiviate comprese', { n: dati.total.toLocaleString(locale()) })
                : t('{n} trattative', { n: dati.total.toLocaleString(locale()) })
              : elenco.error
                ? t('Elenco non disponibile')
                : t('Un momento…')}
          </div>
        </div>
        <div className="page-actions">
          <button
            className="btn"
            // con un filtro sbagliato l'elenco dice già perché: il file aprirebbe il JSON dell'errore
            disabled={Boolean(elenco.error)}
            onClick={() => esportaTrattative(ricerca, 'csv')}>
            CSV
          </button>
          <button
            className="btn"
            // con un filtro sbagliato l'elenco dice già perché: il file aprirebbe il JSON dell'errore
            disabled={Boolean(elenco.error)}
            onClick={() => esportaTrattative(ricerca, 'xlsx')}>
            Excel
          </button>
          <button className="btn btn-primary" onClick={() => setNuovaAperta(true)}>
            {t('Nuova trattativa')}
          </button>
        </div>
      </div>

      {/* accanto al numero c'è l'importo: tre trattative da diecimila e una da
          centomila sono quattro righe, ma non è la stessa settimana */}
      <div className="statebar">
        <button
          className={scelta === undefined ? 'state-tab state-tab-on' : 'state-tab'}
          onClick={() => filtra('status', undefined)}
        >
          {t('Tutte')}
          {/* la somma delle linguette: il totale dell'elenco, con uno stato
              scelto, era il numero di quello stato */}
          <b>{(conteggi.data?.counts ?? []).reduce((s, c) => s + c.count, 0).toLocaleString('it-IT')}</b>
        </button>
        {(conteggi.data?.counts ?? []).map((c) => (
          <button
            key={c.status}
            className={scelta === c.status ? 'state-tab state-tab-on' : 'state-tab'}
            onClick={() => filtra('status', scelta === c.status ? undefined : c.status)}
          >
            {c.label}
            <b>{c.count.toLocaleString('it-IT')}</b>
            {Number(c.amount) > 0 && (
              <span className="dim" style={{ fontSize: 11, marginLeft: 6 }}>
                {euro(Number(c.amount))} · {t('pesato')} {euro(Number(c.weighted))}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="filterbar">
        <span className="omnisearch" style={{ maxWidth: 340, flex: '0 1 340px' }}>
          <span>⌕</span>
          <input
            className="input"
            style={{ border: 'none', height: 22, padding: 0, background: 'transparent' }}
            placeholder={t('Cerca per oggetto, cliente, esigenze, note')}
            value={testo}
            onChange={(e) => setTesto(e.target.value)}
          />
        </span>

        <button
          className={ricerca.archived ? 'chip chip-set' : 'chip'}
          onClick={() => cambia({ archived: !ricerca.archived })}
        >
          {ricerca.archived ? t('Con le archiviate') : 'Mostra archiviate'}
        </button>

        <BarraFiltri
          definizioni={definizioni.data?.filters ?? []}
          opzioni={definizioni.data?.options ?? {}}
          valori={ricerca.filters}
          onCambia={filtra}
        />
      </div>

      <AzioniDiMassa
        percorso="/api/negotiations/bulk"
        nome="trattative"
        selezione={selezione}
        conElimina
        conRipristina={ricerca.archived}
        chiaviDaRileggere={['trattative', 'trattative-conteggi']}
        onFatto={() => setSelezione(new Set())}
        onAnnulla={() => setSelezione(new Set())}
      />

      <div className="table-wrap" style={{ marginTop: 12, marginBottom: 24 }}>
        <Tabella
          colonne={COLONNE}
          righe={dati?.rows ?? []}
          chiaveRiga={(r) => r.id}
          ordina={ricerca.sort}
          verso={ricerca.dir}
          onOrdina={ordinaPer}
          selezione={selezione}
          onSelezione={setSelezione}
          caricamento={elenco.isFetching}
          errore={elenco.error}
          vuoto={
            ricerca.q
              ? t('Nessuna trattativa per «{cerca}».', { cerca: ricerca.q })
              : t('Nessuna trattativa con questi filtri.')
          }
        />
        <PiedeElenco
          pagina={dati?.page ?? 1}
          pagine={dati?.pages ?? 1}
          totale={dati?.total ?? 0}
          dimensione={ricerca.size}
          nomePlurale={t('trattative')}
          onPagina={vaiA}
          onDimensione={(size) => cambia({ size })}
        />
      </div>

      {nuovaAperta && (
        <ModaleTrattativa
          trattativa={null}
          onChiudi={() => setNuovaAperta(false)}
          onCreata={(id) => {
            setNuovaAperta(false);
            void navigate({ to: '/negotiations/$id', params: { id: String(id) } });
          }}
        />
      )}
    </>
  );
}
